// Perzistentní cache výsledků buildGroupTree (IndexedDB přes idb-keyval).
//
// buildGroupTree + k-means clustering jsou drahé a běží na hlavním vlákně. Sync serveru mění
// členství skupin max 1× denně v noci, takže výsledek stačí zapamatovat a příště jen načíst.
//
// Klíč = savedGroupId + hash definice (groupLevels). Změna definice skupiny adminem → jiný hash →
// automatický cache miss. TTL 20 h ≈ „platí do dalšího nočního syncu"; nová stanice se v uložené
// skupině projeví max s jednodenním zpožděním (akceptováno).
import { get as idbGet, set as idbSet, del as idbDel, keys as idbKeys } from 'idb-keyval'

const PREFIX = 'groupTree:'
const TTL_MS = 20 * 60 * 60 * 1000
const MAX_ENTRIES = 60

// 32bitový FNV-1a string hash
function hashString(str) {
  let h = 0x811c9dc5
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return (h >>> 0).toString(36)
}

export function groupTreeCacheKey(savedGroupId, groupLevels) {
  return `${PREFIX}${savedGroupId}:${hashString(JSON.stringify(groupLevels))}`
}

// Strom má uzly { label, ghIds: Set, children: [] } — Set se neserializuje, převádíme na pole.
function serializeNode(node) {
  return { label: node.label, ghIds: [...node.ghIds], children: node.children.map(serializeNode) }
}
function deserializeNode(node) {
  return { label: node.label, ghIds: new Set(node.ghIds), children: node.children.map(deserializeNode) }
}

export async function getCachedGroupTree(key) {
  try {
    const entry = await idbGet(key)
    if (!entry) return null
    if (Date.now() - entry.savedAt > TTL_MS) {
      idbDel(key).catch(() => {})
      return null
    }
    return entry.tree.map(deserializeNode)
  } catch {
    return null
  }
}

export async function setCachedGroupTree(key, tree) {
  try {
    await idbSet(key, { savedAt: Date.now(), tree: tree.map(serializeNode) })
    pruneOldEntries().catch(() => {})
  } catch {
    // IndexedDB nedostupná (private mode apod.) — cache prostě nefunguje, není to chyba
  }
}

// Drž jen MAX_ENTRIES nejnovějších klíčů (LRU podle savedAt) + smaž prošlé
async function pruneOldEntries() {
  const allKeys = (await idbKeys()).filter(k => typeof k === 'string' && k.startsWith(PREFIX))
  if (allKeys.length <= MAX_ENTRIES) return
  const entries = await Promise.all(
    allKeys.map(async k => ({ k, savedAt: (await idbGet(k))?.savedAt ?? 0 }))
  )
  entries.sort((a, b) => b.savedAt - a.savedAt)
  await Promise.all(entries.slice(MAX_ENTRIES).map(e => idbDel(e.k)))
}

// Smaž všechny uložené stromy skupin. Vrací počet smazaných záznamů.
export async function clearGroupTreeCache() {
  try {
    const allKeys = (await idbKeys()).filter(k => typeof k === 'string' && k.startsWith(PREFIX))
    await Promise.all(allKeys.map(k => idbDel(k)))
    return allKeys.length
  } catch {
    return 0
  }
}
